/**
 * Hook: useVoucherTemplateSettings (nested per-template — 2026-07-20 Phase 2)
 * Fetches per-tenant nested voucher template settings.
 *
 * Shape returned by backend:
 *   { simple: {...}, classic: {...}, "classic-orange": {...}, "classic-blue": {...}, compact: {...} }
 *
 * API surface:
 *   { settings, loading, saving, error, save(patchNestedOrFlat), saveTemplate(templateKey, patchFlat), refresh() }
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'rl_vt_settings_v2';

// Must mirror backend defaults
const COMMON = Object.freeze({
  font_brand: 7.0,
  font_plan: 12.0,
  font_label: 10.0,
  font_code: 16.0,
  plan_format: '{duration} - {price}',
  label_text: 'Voucher Code',
  prefix_text: 'No.',
  brand_position: 'footer',
});

export const VT_TEMPLATE_KEYS = ['simple', 'classic', 'classic-orange', 'classic-blue', 'compact', 'qr'];

export const VT_DEFAULTS = Object.freeze({
  simple: { ...COMMON, font_brand: 7.0, font_plan: 12.0, font_label: 10.0, font_code: 16.0, brand_position: 'footer' },
  classic: { ...COMMON, font_brand: 7.0, font_plan: 6.0, font_label: 6.0, font_code: 7.0, prefix_text: 'No.Voucher :' },
  'classic-orange': { ...COMMON, font_brand: 7.0, font_plan: 6.0, font_label: 6.0, font_code: 7.0, prefix_text: 'No.Voucher :' },
  'classic-blue': { ...COMMON, font_brand: 7.0, font_plan: 6.0, font_label: 6.0, font_code: 7.0, prefix_text: 'No.Voucher :' },
  compact: { ...COMMON, font_brand: 4.5, font_plan: 8.5, font_label: 6.5, font_code: 8.5, label_text: 'di-distribusikan oleh' },
  qr: { ...COMMON, font_brand: 5.0, font_plan: 6.5, font_label: 5.5, font_code: 9.0, label_text: 'KODE VOUCHER', plan_format: '{price} / {duration}', prefix_text: 'Distributed by' },
});

const cloneDefaults = () => JSON.parse(JSON.stringify(VT_DEFAULTS));

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && typeof p === 'object' ? p : null;
  } catch { return null; }
};
const writeCache = (s) => { try { localStorage.setItem(CACHE_KEY, JSON.stringify(s)); } catch {} };

// Deep-merge a partial nested object into defaults, only keeping known keys
const mergeIntoDefaults = (partial) => {
  const merged = cloneDefaults();
  if (!partial || typeof partial !== 'object') return merged;
  for (const tk of VT_TEMPLATE_KEYS) {
    const sub = partial[tk];
    if (sub && typeof sub === 'object') {
      for (const k of Object.keys(COMMON)) {
        if (k in sub) merged[tk][k] = sub[k];
      }
    }
  }
  return merged;
};

export const useVoucherTemplateSettings = () => {
  const cached = readCache();
  const [settings, setSettings] = useState(mergeIntoDefaults(cached));
  const [loading, setLoading] = useState(!cached);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fetched = useRef(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) { setLoading(false); return; }
      const res = await axios.get(`${API_URL}/api/v2/voucher-template-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const s = res?.data?.settings;
      if (s && typeof s === 'object') {
        const merged = mergeIntoDefaults(s);
        setSettings(merged);
        writeCache(merged);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'fetch error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Save full nested payload (deep merged server-side)
  const save = useCallback(async (patch) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');
      const res = await axios.put(
        `${API_URL}/api/v2/voucher-template-settings`,
        patch || {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const s = res?.data?.settings;
      if (s && typeof s === 'object') {
        const merged = mergeIntoDefaults(s);
        setSettings(merged);
        writeCache(merged);
      }
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'save error';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setSaving(false);
    }
  }, []);

  // Convenience: save one template's flat fields.
  const saveTemplate = useCallback((templateKey, flatFields) => {
    return save({ [templateKey]: flatFields });
  }, [save]);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    fetchSettings();
  }, [fetchSettings]);

  return { settings, setSettings, loading, saving, error, save, saveTemplate, refresh: fetchSettings };
};

export default useVoucherTemplateSettings;
